// src/pages/PatientPage/PatientPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import Popup from '../../components/Popup/Popup';
import Footer from '../../components/Footer';
import matchmedicLogo from '../../images/matchmedic_logo3.png';

// ---------------------------------------------------------------------------
// STATUS
// ---------------------------------------------------------------------------

const STATUS = {
  LOADING:      'loading',
  ARCHIVED:     'archived',
  CONFIG_ERROR: 'config_error',
  LOCKED:       'locked',
  UNLOCKED:     'unlocked',
};

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

function countryCodeToEmoji(code) {
  if (!code || code.length !== 2) return '';
  return code.toUpperCase().split('').map(
    ch => String.fromCodePoint(ch.charCodeAt(0) - 65 + 0x1F1E6)
  ).join('');
}

function getCountryName(code) {
  if (!code) return '';
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase());
  } catch {
    return code.toUpperCase();
  }
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
}

function formatBudget(raw) {
  if (!raw) return '—';
  if (raw.startsWith('<')) return `Under $${Number(raw.slice(1)).toLocaleString()}`;
  if (raw.startsWith('>')) return `Over $${Number(raw.slice(1)).toLocaleString()}`;
  const parts = raw.split('-');
  if (parts.length === 2) {
    return `$${Number(parts[0]).toLocaleString()} – $${Number(parts[1]).toLocaleString()}`;
  }
  return raw;
}

function cap(str) {
  if (!str) return '—';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function buildPatient(documentId, data) {
  const treatments = Array.isArray(data.past_treatments)
    ? data.past_treatments.join(', ')
    : (data.past_treatments || '—');

  const imgs = data.images || [];
  const mainImg = imgs.find(img => img.id === data.main_image_id) || imgs[0];
  const mainPhotoIndex = mainImg ? Math.max(0, imgs.indexOf(mainImg)) : 0;

  return {
    refCode: documentId,
    reportDate: formatTimestamp(data.created_at),
    name: data.name || '—',
    birthDate: data.birthdate || '—',
    nationality: getCountryName(data.country),
    language: data.language || '—',
    hairLossStart: data.symptoms_start || '—',
    previousTreatments: treatments,
    photo: mainImg ? mainImg.url : null,
    mainPhotoIndex,
    flagEmoji: countryCodeToEmoji(data.country),
    clinicJoinLink: (() => {
      const raw = data.clinic_join_link;
      if (!raw) return null;
      return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    })(),

    contact: {
      preferredChannel: data.preferred_channel || '—',
      status: data.contact_status || '—',
      email: data.email || null,
    },

    budget: formatBudget(data.budget),

    preop: [
      { question: 'Name',                                                          answer: data.name || '—' },
      { question: 'Phone number',                                                  answer: data.phone || '—' },
      { question: 'Email',                                                         answer: data.email || '—' },
      { question: 'Age',                                                           answer: data.age != null ? String(data.age) : '—' },
      { question: 'Chronic illness? (Heart disease, diabetes, etc.)',              answer: cap(data.is_chronic_illness) },
      { question: 'Regular medications? (Insulin, anticoagulants, etc.)',          answer: cap(data.is_regular_medication) },
      { question: 'Smoker?',                                                       answer: cap(data.is_smoker) },
      { question: 'Drug addiction?',                                               answer: cap(data.is_drug_addiction) },
      { question: 'Allergies?',                                                    answer: cap(data.is_allergies) },
      { question: 'Previous surgery? (Including hair transplant)',                 answer: cap(data.is_previous_surgery) },
      { question: 'Needle phobia / low pain threshold?',                           answer: cap(data.is_needle_phobia) },
      { question: 'Genetic heart condition in family?',                            answer: cap(data.is_genetic_heart_condition) },
      { question: 'Local or general anaesthesia in the last 2 years?',            answer: cap(data.is_recent_anesthesia) },
      { question: 'Contagious disease? (Hepatitis A/B/C, HIV)',                   answer: cap(data.is_contagious_disease) },
      { question: 'Anemia diagnosis?',                                             answer: cap(data.is_anemia_diagnosis) },
      { question: 'History of keloid scars?',                                     answer: cap(data.is_keloid_scars) },
      { question: 'Preferred operation timeline',                                  answer: data.preferred_operation_timeline || '—' },
      { question: 'Passport country',                                              answer: getCountryName(data.country) || '—' },
      { question: 'Referral source',                                               answer: data.patient_referral || '—' },
    ],

    photos: (data.images || []).map(img => img.url),
  };
}

// ---------------------------------------------------------------------------
// GALLERY POPUP
// ---------------------------------------------------------------------------

function PatientGalleryPopup({ isOpen, onClose, photos, initialIndex = 0 }) {
  const [current, setCurrent]       = useState(initialIndex);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => { setCurrent(initialIndex); setIsExpanded(false); }, [initialIndex, isOpen]);

  const prev = useCallback(() => setCurrent(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setCurrent(i => Math.min(photos.length - 1, i + 1)), [photos.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, prev, next]);

  const photo = photos[current];

  return (
    <Popup isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <div
          className={`w-full bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center mb-4 relative transition-all duration-300 ${
            isExpanded ? 'h-[72vh]' : 'h-[420px]'
          }`}
        >
          {photo
            ? <img src={photo} alt={`Photo ${current + 1}`} className="w-full h-full object-contain" />
            : <svg className="w-14 h-14 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
          }
          {photos.length > 1 && (
            <>
              <button onClick={prev} disabled={current === 0}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:opacity-25 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              </button>
              <button onClick={next} disabled={current === photos.length - 1}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:opacity-25 transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
            </>
          )}
          {/* Expand/collapse — desktop only */}
          <button
            onClick={() => setIsExpanded(e => !e)}
            className="hidden md:flex absolute top-3 left-3 w-8 h-8 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
            aria-label={isExpanded ? 'Collapse image' : 'Expand image'}
          >
            {isExpanded
              ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 9L4 4m0 0h5m-5 0v5M15 9l5-5m0 0h-5m5 0v5M9 15l-5 5m0 0h5m-5 0v-5M15 15l5 5m0 0h-5m5 0v-5" /></svg>
              : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
            }
          </button>
          <div className="absolute bottom-3 right-3 text-[11px] bg-black/40 text-white px-2 py-0.5 rounded-full">
            {current + 1} / {photos.length}
          </div>
        </div>
        {!isExpanded && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {photos.map((ph, i) => (
              <button key={i} onClick={() => setCurrent(i)}
                className={`w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center transition-all ${
                  i === current ? 'ring-2 ring-gray-900 opacity-100' : 'opacity-50 hover:opacity-80'
                }`}>
                {ph
                  ? <img src={ph} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                  : <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                }
              </button>
            ))}
          </div>
        )}
      </div>
    </Popup>
  );
}

// ---------------------------------------------------------------------------
// SHARED PRIMITIVES
// ---------------------------------------------------------------------------

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function SectionLabel({ children }) {
  return <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">{children}</p>;
}

function InfoTable({ rows }) {
  return (
    <table className="w-full text-xs border-separate border-spacing-0">
      <tbody>
        {rows.filter(Boolean).map(([label, value]) => (
          <tr key={label}>
            <td className="py-[3px] pr-4 text-gray-400 align-top whitespace-nowrap">{label}</td>
            <td className="py-[3px] text-gray-900 font-medium leading-snug">{value || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AssessmentRow({ question, answer }) {
  const isNotable = answer && answer !== 'No' && answer !== '—' && answer !== '-';
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
      <span className="text-[11px] text-gray-500 leading-snug">{question}</span>
      <span className={`text-[11px] font-medium shrink-0 ml-2 ${isNotable ? 'text-gray-900' : 'text-gray-400'}`}>
        {answer}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PASSWORD GATE
// ---------------------------------------------------------------------------

function PasswordGate({ onUnlock }) {
  const [value, setValue]     = useState('');
  const [showPw, setShowPw]   = useState(false);
  const [error, setError]     = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const ok = onUnlock(value);
    if (!ok) setError(true);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-xs space-y-8">
        <img src={matchmedicLogo} alt="MatchMedic" className="h-6 w-auto" />
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-gray-900">Access Patient Record</h2>
          <p className="text-xs text-gray-400 leading-relaxed">
            This record is protected. Enter the access password provided by your matchmedic.com team
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="relative">
            <input
              type={showPw ? 'text' : 'password'}
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(false); }}
              placeholder="Password"
              autoComplete="current-password"
              autoFocus
              className={`w-full px-4 py-3 pr-10 text-sm rounded-xl border bg-gray-50 outline-none transition-colors ${
                error
                  ? 'border-red-300 focus:border-red-400'
                  : 'border-gray-200 focus:border-gray-400'
              }`}
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPw(p => !p)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPw
                ? <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              }
            </button>
          </div>
          {error && (
            <p className="text-xs text-red-500">Incorrect password. Please try again.</p>
          )}
          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 active:bg-black transition-colors"
          >
            Access Record
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN PAGE
// ---------------------------------------------------------------------------

const GALLERY_PREVIEW = 6;

export default function PatientPage() {
  const { documentId } = useParams();

  const [status, setStatus]           = useState(STATUS.LOADING);
  const [patient, setPatient]         = useState(null);
  const [correctPassword, setCorrectPassword] = useState(null);
  const [galleryOpen, setGalleryOpen]   = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    if (!documentId) { setStatus(STATUS.CONFIG_ERROR); return; }
    (async () => {
      try {
        // Step 1: fetch patient report
        const reportSnap = await getDoc(doc(db, 'patient_reports', documentId));
        if (!reportSnap.exists()) { setStatus(STATUS.CONFIG_ERROR); return; }

        const data = reportSnap.data();

        // Step 2: archived check
        if (data.archived === true) { setStatus(STATUS.ARCHIVED); return; }

        // Step 3: fetch clinic template for password
        const templateId = data.clinic_template_id;
        if (!templateId) { setStatus(STATUS.CONFIG_ERROR); return; }

        const templateSnap = await getDoc(doc(db, 'clinic_templates', templateId));
        if (!templateSnap.exists()) { setStatus(STATUS.CONFIG_ERROR); return; }

        const password = templateSnap.data().password;
        if (!password) { setStatus(STATUS.CONFIG_ERROR); return; }

        // All checks passed — load patient data into memory, show lock screen
        setPatient(buildPatient(documentId, data));
        setCorrectPassword(password);
        setStatus(STATUS.LOCKED);
      } catch (e) {
        console.error('[PatientPage] error:', e);
        setStatus(STATUS.CONFIG_ERROR);
      }
    })();
  }, [documentId]);

  const handleUnlock = useCallback((attempt) => {
    if (attempt === correctPassword) { setStatus(STATUS.UNLOCKED); return true; }
    return false;
  }, [correctPassword]);

  const openGallery = (i) => { setGalleryIndex(i); setGalleryOpen(true); };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (status === STATUS.LOADING) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <svg className="animate-spin w-8 h-8 text-gray-300" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>
  );

  // ── Archived ─────────────────────────────────────────────────────────────
  if (status === STATUS.ARCHIVED) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="text-center max-w-xs space-y-2">
        <img src={matchmedicLogo} alt="MatchMedic" className="h-6 w-auto mx-auto mb-6" />
        <p className="text-sm font-medium text-gray-700">This patient report is archived.</p>
        <p className="text-xs text-gray-400">Please contact the matchmedic.com team for assistance.</p>
      </div>
    </div>
  );

  // ── Configuration error ───────────────────────────────────────────────────
  if (status === STATUS.CONFIG_ERROR) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="text-center max-w-xs space-y-2">
        <img src={matchmedicLogo} alt="MatchMedic" className="h-6 w-auto mx-auto mb-6" />
        <p className="text-sm font-medium text-gray-700">Something went wrong.</p>
        <p className="text-xs text-gray-400">Please contact the matchmedic.com team for assistance.</p>
      </div>
    </div>
  );

  // ── Password gate ─────────────────────────────────────────────────────────
  if (status === STATUS.LOCKED) return <PasswordGate onUnlock={handleUnlock} />;

  // ── Unlocked — render patient record ─────────────────────────────────────
  const p = patient;
  const whatsappUrl = p.clinicJoinLink;
  const half = Math.ceil(p.preop.length / 2);
  const leftPreop  = p.preop.slice(0, half);
  const rightPreop = p.preop.slice(half);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{p.name} — Patient Record | Match Medic</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="px-4 sm:px-8 md:px-12 lg:px-24 xl:px-[15%] py-8 space-y-8">

        {/* ── Page header ───────────────────────────────────────────── */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center justify-between sm:justify-start gap-4 w-full sm:w-auto">
            <img src={matchmedicLogo} alt="MatchMedic" className="w-auto h-7 md:h-5" />
            <h1 className="text-sm font-medium text-gray-400">Patient Record</h1>
          </div>
          <div className="flex flex-wrap items-center justify-between sm:justify-start gap-x-4 gap-y-1 text-xs text-gray-400">
            <span>Ref&nbsp;<span className="font-mono text-gray-700 select-all">{p.refCode}</span></span>
            <span className="hidden sm:inline text-gray-200">|</span>
            <span>Date&nbsp;<span className="text-gray-700">{p.reportDate}</span></span>
          </div>
        </div>

        {/* ── Overview + Gallery ────────────────────────────────────── */}
        <div className="flex flex-wrap gap-6">

          {/* Photo + basic info */}
          <div className="flex gap-6 flex-1 min-w-[260px]">
            <div className="w-[112px] shrink-0">
              <button
                onClick={() => p.photos.length > 0 && openGallery(p.mainPhotoIndex)}
                className={`w-full aspect-[3/4] rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center transition-opacity ${p.photos.length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
              >
                {p.photo
                  ? <img src={p.photo} alt={p.name} className="w-full h-full object-cover" />
                  : <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                }
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-900 leading-tight">{p.name}</h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-base leading-none">{p.flagEmoji}</span>
                  <span className="text-sm text-gray-500">{p.nationality}</span>
                </div>
              </div>
              <InfoTable rows={[
                ['Date of Birth', p.birthDate],
                ['Language',      p.language],
                ['Hair Loss Onset', p.hairLossStart],
                ['Prev. Treatments', p.previousTreatments],
              ]} />
            </div>
          </div>

          <div className="hidden lg:block w-px bg-gray-100 self-stretch" />

          {/* Contact + budget */}
          <div className="flex-1 min-w-[200px] space-y-5">
            <div>
              <SectionLabel>Contact</SectionLabel>
              <InfoTable rows={[
                ['Channel', p.contact.preferredChannel],
                ['Status',  p.contact.status],
                p.contact.email && ['Email', p.contact.email],
              ]} />
              {whatsappUrl && (
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] text-white text-[11px] font-medium rounded-lg hover:bg-[#1fb855] transition-colors">
                  <WhatsAppIcon />
                  Open in WhatsApp
                </a>
              )}
            </div>
            <div>
              <SectionLabel>Hair Transplant Budget</SectionLabel>
              <p className="text-2xl font-semibold text-gray-900">{p.budget}</p>
            </div>
          </div>

          <div className="hidden lg:block w-px bg-gray-100 self-stretch" />

          {/* Gallery */}
          <div className="w-56 min-w-[200px] shrink-0">
            <SectionLabel>Attachments · {p.photos.length} photos</SectionLabel>
            {p.photos.length > 0 ? (
              <>
                <div className="grid grid-cols-3 gap-1.5">
                  {p.photos.slice(0, GALLERY_PREVIEW).map((photo, i) => {
                    const isOverflow = i === GALLERY_PREVIEW - 1 && p.photos.length > GALLERY_PREVIEW;
                    return (
                      <button key={i} onClick={() => openGallery(i)}
                        className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center hover:opacity-75 transition-opacity focus:outline-none">
                        {photo
                          ? <img src={photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                          : <svg className="w-5 h-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        }
                        {isOverflow && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                            <span className="text-white text-xs font-semibold">+{p.photos.length - GALLERY_PREVIEW + 1}</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => openGallery(0)}
                  className="mt-2 text-[11px] text-gray-400 hover:text-gray-700 transition-colors">
                  View all photos →
                </button>
              </>
            ) : (
              <p className="text-xs text-gray-400">No attachments</p>
            )}
          </div>

        </div>

        <hr className="border-gray-100" />

        {/* ── Pre-operative assessment ──────────────────────────────── */}
        <div>
          <SectionLabel>Pre-operative Assessment</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
            <div>{leftPreop.map((item, i)  => <AssessmentRow key={i} {...item} />)}</div>
            <div>{rightPreop.map((item, i) => <AssessmentRow key={i} {...item} />)}</div>
          </div>
        </div>

      </div>

      <PatientGalleryPopup
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        photos={p.photos}
        initialIndex={galleryIndex}
      />

      {/* Legal disclaimer */}
      <div className="px-4 sm:px-8 md:px-12 lg:px-24 xl:px-[15%] py-8 border-t border-gray-100 text-center">
        <p className="text-[10px] text-gray-400 leading-relaxed max-w-3xl mx-auto">
          Match Medic Sağlık Yazılım ve Bilişim Anonim Şirketi, {new Date().getFullYear()} ©. The information contained in this report has been submitted by the prospective patient and may not fully or partially reflect the truth. Match Medic Sağlık Yazılım ve Bilişim Anonim Şirketi makes no warranty or guarantee regarding the information contained in this report. Nothing in this report constitutes a medical diagnosis, medical inference, or medical advice, and cannot be characterised as such.
        </p>
      </div>

      <Footer />
    </div>
  );
}
